import React, { useState, useEffect, useRef } from "react";
import MDEditor from "@uiw/react-md-editor";
import ReactQuill from "react-quill";
import "react-quill/dist/quill.snow.css";
import "@uiw/react-md-editor/markdown-editor.css";
import "@uiw/react-markdown-preview/markdown.css";
import MarkdownPreview from "@uiw/react-markdown-preview";
import api from "../../services/api";
import { Trash2, ChevronDown, ChevronUp } from "lucide-react";
import NodeActionsSection from "./NodeActionsSection";
import ActionsReference from "../actions/ActionsReference";
import { conditionRulesApi } from "../../services/api";
import type {
  ConditionRule,
  ConditionRuleCreate,
  ConditionRuleUpdate,
} from "../../types/node_group";
import ValuesListEditor from "../ui/ValuesListEditor";
import { Input, Textarea, Select } from "../ui/Input";

// Debug helper
const debug = (...args: any[]) => {
  try {
    // Opt-in via VITE_DEBUG_UI or default to true in non-production
    const isEnabled = (() => {
      try {
        if (typeof import.meta !== "undefined" && import.meta.env) {
          if (import.meta.env.VITE_DEBUG_UI === "true") return true;
          if (import.meta.env.VITE_DEBUG_UI === "false") return false;
          return (
            (import.meta.env.MODE || import.meta.env.NODE_ENV) !== "production"
          );
        }
      } catch {}
      return true;
    })();
    if (isEnabled) console.debug("[NodeForm]", ...args);
  } catch {}
};

interface LocalizedText {
  en?: string;
  es?: string;
}

interface OptionTrack {
  name?: string;
  type?: "click";
  data?: Record<string, any>;
}

interface ButtonOption {
  text: string | LocalizedText;
  value: string;
  mailto?: string;
  phone?: string;
  link?: string;
  is_call_to_action?: boolean;
  cta_link_type?: "mailto" | "phone" | "link";
  // New: per-option tracking
  track?: OptionTrack;
  _track_enabled?: boolean; // UI helper to toggle tracking
  _track_data_json?: string; // UI helper to edit JSON data
  // New: visibility controls
  hide_when?: boolean; // if true, apply conditions and hide when they fail (AND semantics).
  condition_rules_names?: string[]; // global named rules
  condition_rules_ids?: number[]; // local rules persisted as ids if created globally later
  _local_rules?: import("../../types/node_group").ConditionRule[]; // UI-only for local editing
  _visibility_mode?: "none" | "global" | "local"; // UI helper
  // New: button actions
  actions?: ButtonAction[];
}

// New: Button action interface
interface ButtonAction {
  name: string; // references name in the actions tables (sms, email, run_query)
  event: string; // event that triggers the action (e.g., "click")
  type: "sms" | "email" | "run_query"; // action type
}

// Marks options that already have condition rules to auto-enable hide toggle
const prepareOptionForUI = (opt: any): ButtonOption => {
  const hasNames =
    Array.isArray(opt?.condition_rules_names) &&
    (opt.condition_rules_names as any[]).length > 0;
  const hasIds =
    Array.isArray(opt?.condition_rules_ids) &&
    (opt.condition_rules_ids as any[]).length > 0;
  const hide = !!opt?.hide_when || hasNames || hasIds;
  const mode: "global" | "local" | "none" = hasNames
    ? "global"
    : hide
      ? "global"
      : "none";

  // Initialize tracking UI helpers
  const hasTrack = !!opt?.track;
  let trackDataJson: string | undefined = undefined;
  try {
    trackDataJson = opt?.track?.data
      ? JSON.stringify(opt.track.data, null, 2)
      : "";
  } catch {
    trackDataJson = "";
  }

  return {
    ...(opt as ButtonOption),
    hide_when: hide,
    _visibility_mode: (opt?._visibility_mode as any) || mode,
    _track_enabled: hasTrack,
    _track_data_json: (opt as any)?._track_data_json ?? trackDataJson ?? "",
    actions: opt?.actions || [], // Ensure actions array is initialized
  } as ButtonOption;
};

interface TrackConfig {
  type: string;
  html_element: string;
}

interface Node {
  id?: number;
  node_name: string;
  script_id?: number;
  messages?: Record<string, string>;
  input_type?: string;
  input_data?: Record<string, any>;
  tracks?: TrackConfig[];
  capture_rules?: Record<string, any>[];
  message_seq_no?: string;
}

interface TestingConfig {
  conversation_key?: string;
  session_id?: string;
}

interface NodeFormProps {
  node?: Node;
  onSubmit: (node: Omit<Node, "id">) => void;
  onCancel: () => void;
  isSubmitting?: boolean;
  scripts?: Array<{ id: number; name: string }>;
  nodes?: Array<{ id: number; node_name: string }>;
  testingConfig?: TestingConfig;
  onChange?: (data: Omit<Node, "id">, dirty: boolean) => void;
  extraSectionBeforeActions?: React.ReactNode;
  showScriptSelect?: boolean;
  onDelete?: () => void;
  onlyShowExtraSection?: boolean;
  // New: when true, disable editing and submitting
  readOnly?: boolean;
}

const INPUT_TYPES = [
  { value: "text", label: "Text Input" },
  { value: "email", label: "Email Input" },
  { value: "phone", label: "Phone Input" },
  { value: "number", label: "Number Input" },
  { value: "money", label: "Money Input" },
  { value: "buttons", label: "Buttons" },
  { value: "date-picker", label: "Date Picker" },
];

const LANGUAGES = [
  { code: "en", label: "English" },
  { code: "es", label: "Spanish" },
];

export default function NodeForm({
  node,
  onSubmit,
  onCancel,
  isSubmitting,
  scripts = [],
  nodes = [],
  testingConfig,
  onChange,
  extraSectionBeforeActions,
  showScriptSelect = true,
  onDelete,
  onlyShowExtraSection = false,
  readOnly = false,
}: NodeFormProps) {
  const [formData, setFormData] = useState<Omit<Node, "id">>({
    node_name: "",
    script_id: undefined,
    messages: {},
    input_type: "",
    input_data: {},
    tracks: [],
    capture_rules: [],
    message_seq_no: "",
  });

  const initialDataRef = useRef<Omit<Node, "id"> | null>(null);
  // Cache input_data by input_type to preserve state when toggling types
  const inputDataCacheRef = useRef<Record<string, any>>({});

  const [buttonOptions, setButtonOptions] = useState<ButtonOption[]>([]);
  // New state for global condition rules catalog
  const [globalRules, setGlobalRules] = useState<ConditionRule[]>([]);
  const [globalRulesQuery, setGlobalRulesQuery] = useState<string>("");
  const [isLoadingGlobalRules, setIsLoadingGlobalRules] =
    useState<boolean>(false);
  const [trackConfigs, setTrackConfigs] = useState<TrackConfig[]>([]);
  const [messageSyntax, setMessageSyntax] = useState<
    Record<string, "markdown" | "html">
  >({});
  const [previewStateText, setPreviewStateText] = useState<
    Record<string, string>
  >({});
  const [messagePreview, setMessagePreview] = useState<Record<string, string>>(
    {},
  );
  const [previewLoading, setPreviewLoading] = useState<Record<string, boolean>>(
    {},
  );
  const [previewError, setPreviewError] = useState<
    Record<string, string | null>
  >({});
  const [selectedLanguage, setSelectedLanguage] = useState<string>("en");
  const [selectedInputLanguage, setSelectedInputLanguage] =
    useState<string>("en");
  // Testing Configuration state
  const [testingConversationKey, setTestingConversationKey] =
    useState<string>("");
  const [testingSessionId, setTestingSessionId] = useState<string>("");
  const [basicCollapsed, setBasicCollapsed] = useState(true);
  const [messagesCollapsed, setMessagesCollapsed] = useState(true);
  const [inputCollapsed, setInputCollapsed] = useState(true);
  const [trackingCollapsed, setTrackingCollapsed] = useState(true);

  useEffect(() => {
    if (readOnly) {
      setBasicCollapsed(false);
      setMessagesCollapsed(false);
      setInputCollapsed(false);
      setTrackingCollapsed(false);
    }
  }, [readOnly]);

  useEffect(() => {
    if (node) {
      debug("Initializing form with node", node);
      const next: Omit<Node, "id"> = {
        node_name: node.node_name,
        script_id: node.script_id,
        messages: node.messages || {},
        input_type: node.input_type || "",
        input_data: node.input_data || {},
        tracks: node.tracks || [],
        capture_rules: node.capture_rules || [],
        message_seq_no: node.message_seq_no || "",
      };
      setFormData(next);
      initialDataRef.current = normalizeSubmitData(next, []);

      // Seed cache with current input_data for its type
      if (next.input_type) {
        inputDataCacheRef.current[next.input_type] = next.input_data || {};
      }

      // Initialize button options if input type is buttons
      if (node.input_type === "buttons" && node.input_data?.options) {
        setButtonOptions(
          (node.input_data.options as any[]).map(prepareOptionForUI),
        );
      } else {
        setButtonOptions([]);
      }

      // Initialize track configs
      if (node.tracks) {
        setTrackConfigs(node.tracks);
      } else {
        setTrackConfigs([]);
      }

      // Initialize syntax defaults and preview state per available and supported languages
      const msgs = node.messages || {};
      const firstMsgLang = Object.keys(msgs)[0] || "en";
      setSelectedLanguage(firstMsgLang);
      debug("Detected languages", Object.keys(msgs), "selected", firstMsgLang);

      const nextSyntax: Record<string, "markdown" | "html"> = {
        ...messageSyntax,
      };
      const allLangs = Array.from(
        new Set([...Object.keys(msgs), ...LANGUAGES.map((l) => l.code)]),
      );
      allLangs.forEach((lang) => {
        if (!nextSyntax[lang]) nextSyntax[lang] = "markdown";
      });
      setMessageSyntax(nextSyntax);

      const nextPreviewState: Record<string, string> = { ...previewStateText };
      allLangs.forEach((lang) => {
        if (nextPreviewState[lang] === undefined) nextPreviewState[lang] = "{}";
      });
      setPreviewStateText(nextPreviewState);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [node?.id]);

  // Initialize Testing Configuration from props/localStorage
  useEffect(() => {
    try {
      const storedKey =
        localStorage.getItem("nodeFormTesting.conversation_key") || "";
      const storedSession =
        localStorage.getItem("nodeFormTesting.session_id") || "";
      let globalKey = "";
      let globalSession = "";
      try {
        const globalCfg = JSON.parse(
          localStorage.getItem("globalTestingConfig") || "{}",
        );
        globalKey =
          (globalCfg && typeof globalCfg === "object"
            ? (globalCfg as any).conversation_key
            : "") || "";
        globalSession =
          (globalCfg && typeof globalCfg === "object"
            ? (globalCfg as any).session_id
            : "") || "";
      } catch {}
      setTestingConversationKey(
        testingConfig?.conversation_key || globalKey || storedKey || "",
      );
      setTestingSessionId(
        testingConfig?.session_id || globalSession || storedSession || "",
      );
    } catch {
      // Fallback if localStorage access fails
      setTestingConversationKey(testingConfig?.conversation_key || "");
      setTestingSessionId(testingConfig?.session_id || "");
    }
  }, [testingConfig?.conversation_key, testingConfig?.session_id]);

  // Persist Testing Configuration
  useEffect(() => {
    try {
      localStorage.setItem(
        "nodeFormTesting.conversation_key",
        testingConversationKey || "",
      );
      localStorage.setItem(
        "nodeFormTesting.session_id",
        testingSessionId || "",
      );
    } catch {}
  }, [testingConversationKey, testingSessionId]);

  useEffect(() => {
    // Ensure defaults exist when switching language
    if (!messageSyntax[selectedLanguage]) {
      setMessageSyntax((prev) => ({ ...prev, [selectedLanguage]: "markdown" }));
    }
    if (previewStateText[selectedLanguage] === undefined) {
      setPreviewStateText((prev) => ({ ...prev, [selectedLanguage]: "{}" }));
    }
    debug(
      "Language changed",
      selectedLanguage,
      "syntax",
      messageSyntax[selectedLanguage],
    );
  }, [selectedLanguage]);

  // Load initial global rules list
  useEffect(() => {
    const load = async () => {
      try {
        setIsLoadingGlobalRules(true);
        const res = await conditionRulesApi.list({
          page_size: 200,
          search: globalRulesQuery || undefined,
          global_only: true,
        });
        setGlobalRules(res.rules || []);
      } catch {
      } finally {
        setIsLoadingGlobalRules(false);
      }
    };
    load();
  }, [globalRulesQuery]);

  // Compute available languages in input_data (tip/placeholder/options)
  const getAvailableInputLanguages = (): string[] => {
    const langsSet = new Set<string>(LANGUAGES.map((l) => l.code));
    const idata: any = formData.input_data || {};
    const maybeCollect = (val: any) => {
      if (val && typeof val === "object" && !Array.isArray(val)) {
        Object.keys(val).forEach((k) => langsSet.add(k));
      }
    };
    maybeCollect(idata.tip);
    maybeCollect(idata.placeholder);
    const options = Array.isArray(idata.options) ? idata.options : [];
    options.forEach((opt: any) => {
      if (opt && typeof opt.text === "object" && !Array.isArray(opt.text)) {
        Object.keys(opt.text).forEach((k: string) => langsSet.add(k));
      }
    });
    // Preserve default language order, then extras
    const defaultOrder = LANGUAGES.map((l) => l.code);
    const extras = Array.from(langsSet)
      .filter((c) => !defaultOrder.includes(c))
      .sort();
    return [...defaultOrder, ...extras];
  };

  const handleInputChange = (field: keyof Node, value: any) => {
    debug("Input change", field, value);
    setFormData((prev) => ({ ...prev, [field]: value }));
  };

  const handleMessageChange = (language: string, value: string) => {
    debug("Message change", language, { length: value?.length || 0 });
    setFormData((prev) => ({
      ...prev,
      messages: { ...prev.messages, [language]: value },
    }));
  };

  const handleSyntaxChange = (
    language: string,
    syntax: "markdown" | "html",
  ) => {
    debug("Syntax change", language, syntax);
    setMessageSyntax((prev) => ({ ...prev, [language]: syntax }));
  };

  const previewMessage = async (language: string) => {
    try {
      setPreviewError((prev) => ({ ...prev, [language]: null }));
      setPreviewLoading((prev) => ({ ...prev, [language]: true }));
      const raw = formData.messages?.[language] || "";
      debug("Preview start", { language, length: raw.length });

      let globalConversationKey = "";
      let globalSessionId = "";
      try {
        const globalCfg = JSON.parse(
          localStorage.getItem("globalTestingConfig") || "{}",
        );
        globalConversationKey =
          (globalCfg && typeof globalCfg === "object"
            ? (globalCfg as any).conversation_key
            : "") || "";
        globalSessionId =
          (globalCfg && typeof globalCfg === "object"
            ? (globalCfg as any).session_id
            : "") || "";
      } catch {}
      const conversationKey = (
        testingConversationKey ||
        testingConfig?.conversation_key ||
        globalConversationKey ||
        ""
      ).trim();
      const sessionId = (
        testingSessionId ||
        testingConfig?.session_id ||
        globalSessionId ||
        ""
      ).trim();
      if (!conversationKey) {
        setPreviewError((prev) => ({
          ...prev,
          [language]:
            "Conversation Key is required for preview (set it in Testing Configuration).",
        }));
        setPreviewLoading((prev) => ({ ...prev, [language]: false }));
        debug("Preview aborted: missing conversation_key");
        return;
      }

      const response = await fetch(
        "https://chatbot-api.happysmoke-4e8cbf49.eastus.azurecontainerapps.io/render-template",
        {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({
            conversation_key: conversationKey,
            session_id: sessionId || undefined,
            message: raw,
          }),
        },
      );

      if (!response.ok) {
        const err = await response.json().catch(() => ({}) as any);
        debug("Preview error response", response.status, err);
        throw new Error((err as any)?.detail || "Failed to render template");
      }

      const data = await response.json();
      debug("Preview success", {
        language,
        length: (data as any)?.rendered?.length || 0,
      });
      setMessagePreview((prev) => ({
        ...prev,
        [language]: (data as any).rendered || "",
      }));
    } catch (e: any) {
      debug("Preview exception", e?.message || e);
      setPreviewError((prev) => ({
        ...prev,
        [language]: e?.message || "Failed to preview message",
      }));
    } finally {
      setPreviewLoading((prev) => ({ ...prev, [language]: false }));
    }
  };

  const handleInputDataChange = (field: string, value: any) => {
    debug("Input data change", field, value);
    setFormData((prev) => ({
      ...prev,
      input_data: { ...prev.input_data, [field]: value },
    }));
  };

  // Helpers for localized input_data fields (tip, placeholder)
  const getLocalizedFieldValue = (field: string, language: string): string => {
    const val = (formData.input_data as any)?.[field];
    if (val === undefined || val === null) return "";
    if (typeof val === "string") {
      return language === "en" ? val : "";
    }
    if (typeof val === "object") {
      return (val as Record<string, string>)[language] || "";
    }
    return "";
  };

  const setLocalizedFieldValue = (
    field: string,
    language: string,
    value: string,
  ) => {
    debug("Localized field change", { field, language, value });
    setFormData((prev) => {
      const current = (prev.input_data as any)?.[field];
      let nextFieldVal: any;
      if (current === undefined || current === null) {
        nextFieldVal = { [language]: value };
      } else if (typeof current === "string") {
        if (language === "en") {
          nextFieldVal = value; // keep as string for en-only
        } else {
          nextFieldVal = { en: current, [language]: value };
        }
      } else if (typeof current === "object") {
        nextFieldVal = {
          ...(current as Record<string, string>),
          [language]: value,
        };
      } else {
        nextFieldVal = { [language]: value };
      }
      return {
        ...prev,
        input_data: { ...(prev.input_data || {}), [field]: nextFieldVal },
      };
    });
  };

  const addButtonOption = () => {
    const newOption: ButtonOption = {
      text: "",
      value: "",
      _visibility_mode: "none",
      hide_when: false,
      actions: [], // Initialize with empty actions array
    };
    debug("Add button option");
    setButtonOptions([...buttonOptions, newOption]);
    setFormData((prev) => ({
      ...prev,
      input_data: {
        ...prev.input_data,
        options: [...(prev.input_data?.options || []), newOption],
      },
    }));
  };

  const updateButtonOption = (
    index: number,
    field: keyof ButtonOption,
    value: any,
  ) => {
    debug("Update button option", { index, field, value });
    setButtonOptions((prev) => {
      const next = [...prev];
      next[index] = { ...(next[index] || {}), [field]: value } as ButtonOption;
      return next;
    });

    // Update form data in lockstep with buttonOptions
    setFormData((prev) => {
      const prevOptions: ButtonOption[] = Array.isArray(
        prev.input_data?.options,
      )
        ? prev.input_data.options
        : [];
      const nextOptions = [...prevOptions];
      nextOptions[index] = {
        ...(nextOptions[index] || {}),
        [field]: value,
      } as ButtonOption;
      return {
        ...prev,
        input_data: { ...prev.input_data, options: nextOptions },
      };
    });
  };

  const removeButtonOption = (index: number) => {
    debug("Remove button option", index);
    const updated = buttonOptions.filter((_, i) => i !== index);
    setButtonOptions(updated);
    setFormData((prev) => ({
      ...prev,
      input_data: { ...prev.input_data, options: updated },
    }));
  };

  const addTrackConfig = () => {
    const newTrack: TrackConfig = { type: "click", html_element: "" };
    debug("Add track", newTrack);
    setTrackConfigs([...trackConfigs, newTrack]);
    setFormData((prev) => ({
      ...prev,
      tracks: [...(prev.tracks || []), newTrack],
    }));
  };

  const updateTrackConfig = (
    index: number,
    field: keyof TrackConfig,
    value: string,
  ) => {
    const updated = [...trackConfigs];
    updated[index] = { ...updated[index], [field]: value };
    debug("Update track", { index, field, value });
    setTrackConfigs(updated);
    setFormData((prev) => ({ ...prev, tracks: updated }));
  };

  const removeTrackConfig = (index: number) => {
    debug("Remove track", index);
    const updated = trackConfigs.filter((_, i) => i !== index);
    setTrackConfigs(updated);
    setFormData((prev) => ({ ...prev, tracks: updated }));
  };

  const normalizeSubmitData = (
    data: Omit<Node, "id">,
    tracks: TrackConfig[],
  ): Omit<Node, "id"> => {
    const submitData: Omit<Node, "id"> = { ...data };
    if (!submitData.script_id) submitData.script_id = undefined;
    if (!submitData.input_type) submitData.input_type = undefined;
    if (!submitData.message_seq_no) submitData.message_seq_no = undefined;
    submitData.tracks = tracks.length > 0 ? tracks : undefined;
    submitData.capture_rules =
      submitData.capture_rules && submitData.capture_rules.length > 0
        ? submitData.capture_rules
        : undefined;
    return submitData;
  };

  // Persist local condition rules (if any) and convert to ids, also strip UI-only fields
  const persistLocalRulesForOptions = async (
    options: ButtonOption[],
  ): Promise<ButtonOption[]> => {
    const result: ButtonOption[] = [];
    for (const opt of options) {
      const next: ButtonOption = { ...opt };
      // Create local rules as standalone condition_rules rows and map ids
      if (
        next._visibility_mode === "local" &&
        Array.isArray(next._local_rules) &&
        next._local_rules.length > 0
      ) {
        const createdIds: number[] = [];
        for (const lr of next._local_rules) {
          try {
            const payload: ConditionRuleCreate = {
              type: lr.type,
              values: (lr as any).values ?? null,
              sql_query: (lr as any).sql_query ?? null,
              db_name: (lr as any).db_name ?? null,
              logical_operator: (lr as any).logical_operator,
              use_logical_not: (lr as any).use_logical_not,
              user_input_node_id: (lr as any).user_input_node_id ?? null,
            } as any;
            const created = await conditionRulesApi.create(payload);
            if (created && typeof (created as any).id === "number")
              createdIds.push((created as any).id);
          } catch (e) {
            // best-effort: skip failed creations
          }
        }
        if (createdIds.length > 0) next.condition_rules_ids = createdIds;
      }
      // If user chose global mode, keep names; if none selected, ensure field not set
      if (
        next._visibility_mode === "global" &&
        Array.isArray(next.condition_rules_names) &&
        next.condition_rules_names.length === 0
      ) {
        delete (next as any).condition_rules_names;
      }

      // Normalize per-option tracking: build track from UI helpers
      if ((next as any)._track_enabled) {
        const name = (next as any).track?.name
          ? String((next as any).track.name).trim()
          : undefined;
        const type = (next as any).track?.type || "click";
        let data: Record<string, any> | undefined = undefined;
        const raw = (next as any)._track_data_json;
        if (raw && typeof raw === "string" && raw.trim()) {
          try {
            const parsed = JSON.parse(raw);
            if (
              parsed &&
              typeof parsed === "object" &&
              !Array.isArray(parsed)
            ) {
              data = parsed as Record<string, any>;
            }
          } catch (e) {
            // ignore invalid JSON; omit data
          }
        }
        (next as any).track = { name, type, data } as any;
      } else {
        // If tracking disabled, remove track config
        delete (next as any).track;
      }

      // Clean UI-only fields
      delete (next as any)._track_enabled;
      delete (next as any)._track_data_json;
      delete (next as any)._local_rules;
      delete (next as any)._visibility_mode;
      delete (next as any).hide_when;
      result.push(next);
    }
    return result;
  };

  // Emit onChange with normalized data and dirty flag
  useEffect(() => {
    if (!onChange) return;
    const normalized = normalizeSubmitData(formData, trackConfigs);
    const initial =
      initialDataRef.current || normalizeSubmitData(formData, trackConfigs);
    const dirty = JSON.stringify(normalized) !== JSON.stringify(initial);
    debug("onChange emit", { dirty, normalized });
    onChange(normalized, dirty);
  }, [formData, trackConfigs, onChange]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    let prepared: Omit<Node, "id"> = { ...formData };
    if (
      prepared.input_type === "buttons" &&
      Array.isArray((prepared.input_data as any)?.options)
    ) {
      const original = (prepared.input_data as any).options as ButtonOption[];
      const processed = await persistLocalRulesForOptions(original);
      prepared = {
        ...prepared,
        input_data: { ...(prepared.input_data || {}), options: processed },
      };
    }

    const submitData = normalizeSubmitData(prepared, trackConfigs);
    debug("Submit", submitData);

    onSubmit(submitData);
  };

  // If only the extra section should be shown (e.g., Local Transactions), render just that
  if (onlyShowExtraSection) {
    return <div className="space-y-6">{extraSectionBeforeActions}</div>;
  }

  const renderInputDataFields = () => {
    switch (formData.input_type) {
      case "text":
        return (
          <div className="space-y-4">
            <Input
              label={`Tip Text (${
                LANGUAGES.find((l) => l.code === selectedInputLanguage)
                  ?.label || selectedInputLanguage.toUpperCase()
              })`}
              type="text"
              value={getLocalizedFieldValue("tip", selectedInputLanguage)}
              onChange={(e) =>
                setLocalizedFieldValue(
                  "tip",
                  selectedInputLanguage,
                  e.target.value,
                )
              }
              placeholder="Enter tip text"
            />
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Placeholder (
                {LANGUAGES.find((l) => l.code === selectedInputLanguage)
                  ?.label || selectedInputLanguage.toUpperCase()}
                )
              </label>
              <input
                type="text"
                value={getLocalizedFieldValue(
                  "placeholder",
                  selectedInputLanguage,
                )}
                onChange={(e) =>
                  setLocalizedFieldValue(
                    "placeholder",
                    selectedInputLanguage,
                    e.target.value,
                  )
                }
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                placeholder="Enter placeholder text"
              />
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Min Length
                </label>
                <input
                  type="number"
                  value={formData.input_data?.min_length || ""}
                  onChange={(e) =>
                    handleInputDataChange(
                      "min_length",
                      parseInt(e.target.value) || undefined,
                    )
                  }
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                  min="0"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Max Length
                </label>
                <input
                  type="number"
                  value={formData.input_data?.max_length || ""}
                  onChange={(e) =>
                    handleInputDataChange(
                      "max_length",
                      parseInt(e.target.value) || undefined,
                    )
                  }
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                  min="1"
                />
              </div>
            </div>
          </div>
        );

      case "number":
        return (
          <div className="space-y-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Tip Text (
                {LANGUAGES.find((l) => l.code === selectedInputLanguage)
                  ?.label || selectedInputLanguage.toUpperCase()}
                )
              </label>
              <input
                type="text"
                value={getLocalizedFieldValue("tip", selectedInputLanguage)}
                onChange={(e) =>
                  setLocalizedFieldValue(
                    "tip",
                    selectedInputLanguage,
                    e.target.value,
                  )
                }
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                placeholder="Enter tip text"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Placeholder (
                {LANGUAGES.find((l) => l.code === selectedInputLanguage)
                  ?.label || selectedInputLanguage.toUpperCase()}
                )
              </label>
              <input
                type="text"
                value={getLocalizedFieldValue(
                  "placeholder",
                  selectedInputLanguage,
                )}
                onChange={(e) =>
                  setLocalizedFieldValue(
                    "placeholder",
                    selectedInputLanguage,
                    e.target.value,
                  )
                }
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                placeholder="Enter placeholder text"
              />
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Min Value
                </label>
                <input
                  type="number"
                  value={formData.input_data?.min_value || ""}
                  onChange={(e) =>
                    handleInputDataChange(
                      "min_value",
                      parseFloat(e.target.value) || undefined,
                    )
                  }
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                  step="any"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Max Value
                </label>
                <input
                  type="number"
                  value={formData.input_data?.max_value || ""}
                  onChange={(e) =>
                    handleInputDataChange(
                      "max_value",
                      parseFloat(e.target.value) || undefined,
                    )
                  }
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                  step="any"
                />
              </div>
            </div>
            <div>
              <label className="flex items-center">
                <input
                  type="checkbox"
                  checked={formData.input_data?.allow_decimals !== false}
                  onChange={(e) =>
                    handleInputDataChange("allow_decimals", e.target.checked)
                  }
                  className="mr-2"
                />
                Allow Decimals
              </label>
            </div>
          </div>
        );

      case "money":
        return (
          <div className="space-y-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Tip Text (
                {LANGUAGES.find((l) => l.code === selectedInputLanguage)
                  ?.label || selectedInputLanguage.toUpperCase()}
                )
              </label>
              <input
                type="text"
                value={getLocalizedFieldValue("tip", selectedInputLanguage)}
                onChange={(e) =>
                  setLocalizedFieldValue(
                    "tip",
                    selectedInputLanguage,
                    e.target.value,
                  )
                }
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                placeholder="Enter tip text"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Placeholder (
                {LANGUAGES.find((l) => l.code === selectedInputLanguage)
                  ?.label || selectedInputLanguage.toUpperCase()}
                )
              </label>
              <input
                type="text"
                value={getLocalizedFieldValue(
                  "placeholder",
                  selectedInputLanguage,
                )}
                onChange={(e) =>
                  setLocalizedFieldValue(
                    "placeholder",
                    selectedInputLanguage,
                    e.target.value,
                  )
                }
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                placeholder="Enter placeholder text"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Currency Symbol
              </label>
              <input
                type="text"
                value={formData.input_data?.currency_symbol || "$"}
                onChange={(e) =>
                  handleInputDataChange("currency_symbol", e.target.value)
                }
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                placeholder="$"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Max Length
              </label>
              <input
                type="number"
                value={formData.input_data?.max_length || ""}
                onChange={(e) =>
                  handleInputDataChange(
                    "max_length",
                    parseInt(e.target.value) || undefined,
                  )
                }
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                min="1"
              />
            </div>
          </div>
        );

      case "buttons":
        return (
          <div className="space-y-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Tip Text (
                {LANGUAGES.find((l) => l.code === selectedInputLanguage)
                  ?.label || selectedInputLanguage.toUpperCase()}
                )
              </label>
              <input
                type="text"
                value={getLocalizedFieldValue("tip", selectedInputLanguage)}
                onChange={(e) =>
                  setLocalizedFieldValue(
                    "tip",
                    selectedInputLanguage,
                    e.target.value,
                  )
                }
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                placeholder="Enter tip text"
              />
            </div>

            {/* Actions Reference */}
            <ActionsReference className="mb-4" />

            <div>
              <div className="flex justify-between items-center mb-3">
                <label className="block text-sm font-medium text-gray-700">
                  Button Options
                </label>
                <button
                  type="button"
                  onClick={addButtonOption}
                  className="px-3 py-1 bg-blue-500 text-white rounded-md hover:bg-blue-600 text-sm"
                >
                  Add Button
                </button>
              </div>
              {buttonOptions.map((option, index) => (
                <div
                  key={index}
                  className="flex flex-col gap-2 mb-3 border rounded-md p-2"
                >
                  <div className="flex flex-wrap md:flex-nowrap items-start gap-2">
                    <input
                      type="text"
                      value={
                        typeof option.text === "string"
                          ? option.text
                          : (option.text?.[
                              selectedInputLanguage as keyof LocalizedText
                            ] as string) || ""
                      }
                      onChange={(e) => {
                        const newTextVal = (() => {
                          if (typeof option.text === "string") {
                            if (selectedInputLanguage === "en")
                              return e.target.value;
                            return {
                              en: option.text,
                              [selectedInputLanguage]: e.target.value,
                            } as LocalizedText;
                          }
                          return {
                            ...(option.text || {}),
                            [selectedInputLanguage]: e.target.value,
                          } as LocalizedText;
                        })();
                        updateButtonOption(index, "text", newTextVal);
                      }}
                      placeholder="Button text"
                      className="flex-1 min-w-[200px] px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                    />
                    <input
                      type="text"
                      value={option.value}
                      onChange={(e) =>
                        updateButtonOption(index, "value", e.target.value)
                      }
                      placeholder="Button value"
                      className="flex-1 min-w-[200px] px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                    />
                    <button
                      type="button"
                      onClick={() => removeButtonOption(index)}
                      className="p-2 rounded hover:bg-red-50 text-red-600 self-start shrink-0"
                      title="Remove button option"
                      aria-label="Remove button option"
                    >
                      <Trash2 className="h-4 w-4" />
                    </button>
                  </div>
                  <label className="inline-flex items-center gap-2">
                    <input
                      type="checkbox"
                      checked={!!option.is_call_to_action}
                      onChange={(e) => {
                        const checked = e.target.checked;
                        updateButtonOption(index, "is_call_to_action", checked);
                        if (checked && !option.cta_link_type) {
                          updateButtonOption(index, "cta_link_type", "link");
                        }
                        if (!checked) {
                          updateButtonOption(index, "mailto", undefined);
                          updateButtonOption(index, "phone", undefined);
                          updateButtonOption(index, "link", undefined);
                          updateButtonOption(index, "cta_link_type", undefined);
                        }
                      }}
                    />
                    <span className="text-sm">
                      Call to action (do not send reply)
                    </span>
                  </label>
                  {option.is_call_to_action && (
                    <div className="flex flex-col gap-2">
                      <div className="flex flex-wrap items-center gap-3">
                        <span className="text-xs font-medium text-gray-600">
                          Link type:
                        </span>
                        {(["mailto", "phone", "link"] as const).map((t) => (
                          <label
                            key={t}
                            className="inline-flex items-center gap-1 text-sm"
                          >
                            <input
                              type="radio"
                              name={`cta-type-${index}`}
                              checked={option.cta_link_type === t}
                              onChange={() => {
                                updateButtonOption(index, "cta_link_type", t);
                                // Clear other fields when switching type
                                updateButtonOption(
                                  index,
                                  "mailto",
                                  t === "mailto"
                                    ? option.mailto || ""
                                    : undefined,
                                );
                                updateButtonOption(
                                  index,
                                  "phone",
                                  t === "phone"
                                    ? option.phone || ""
                                    : undefined,
                                );
                                updateButtonOption(
                                  index,
                                  "link",
                                  t === "link" ? option.link || "" : undefined,
                                );
                              }}
                            />
                            <span className="capitalize">{t}</span>
                          </label>
                        ))}
                      </div>

                      {option.cta_link_type === "mailto" && (
                        <input
                          type="text"
                          value={option.mailto || ""}
                          onChange={(e) =>
                            updateButtonOption(
                              index,
                              "mailto",
                              e.target.value || undefined,
                            )
                          }
                          placeholder="mailto (e.g., support@example.com)"
                          className="px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                        />
                      )}
                      {option.cta_link_type === "phone" && (
                        <input
                          type="text"
                          value={option.phone || ""}
                          onChange={(e) =>
                            updateButtonOption(
                              index,
                              "phone",
                              e.target.value || undefined,
                            )
                          }
                          placeholder="phone (e.g., +1-800-123-4567)"
                          className="px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                        />
                      )}
                      {option.cta_link_type === "link" && (
                        <input
                          type="text"
                          value={option.link || ""}
                          onChange={(e) =>
                            updateButtonOption(
                              index,
                              "link",
                              e.target.value || undefined,
                            )
                          }
                          placeholder="link (e.g., https://example.com)"
                          className="px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                        />
                      )}

                      {option.is_call_to_action &&
                        !!option.cta_link_type &&
                        !(
                          (option.cta_link_type === "mailto" &&
                            option.mailto) ||
                          (option.cta_link_type === "phone" && option.phone) ||
                          (option.cta_link_type === "link" && option.link)
                        ) && (
                          <div className="text-xs text-red-600">
                            Please enter a value for the selected link type.
                          </div>
                        )}
                    </div>
                  )}

                  {/* New: Per-option Tracking */}
                  <div className="mt-2 bg-gray-50 rounded p-2">
                    <div className="flex items-center justify-between">
                      <label className="inline-flex items-center gap-2">
                        <input
                          type="checkbox"
                          checked={!!option._track_enabled}
                          onChange={(e) => {
                            const checked = e.target.checked;
                            updateButtonOption(
                              index,
                              "_track_enabled",
                              checked as any,
                            );
                            if (!checked) {
                              updateButtonOption(
                                index,
                                "track",
                                undefined as any,
                              );
                              updateButtonOption(
                                index,
                                "_track_data_json",
                                "" as any,
                              );
                            } else if (!option.track) {
                              updateButtonOption(index, "track", {
                                type: "click",
                              } as any);
                            }
                          }}
                        />
                        <span className="text-sm">
                          Enable per-option tracking
                        </span>
                      </label>
                    </div>

                    {option._track_enabled && (
                      <div className="mt-2 space-y-2">
                        <div className="flex flex-wrap md:flex-nowrap items-start gap-2">
                          <div className="flex-1 min-w-[200px]">
                            <label className="block text-xs text-gray-600 mb-1">
                              Track name (optional)
                            </label>
                            <input
                              type="text"
                              value={option.track?.name || ""}
                              onChange={(e) =>
                                updateButtonOption(index, "track", {
                                  ...(option.track || { type: "click" }),
                                  name: e.target.value,
                                } as any)
                              }
                              placeholder="e.g., cta_download_click or leave blank"
                              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                            />
                            <div className="text-[11px] text-gray-500 mt-1">
                              Defaults to element_click_&lt;value&gt; if blank.
                            </div>
                          </div>
                        </div>
                        <div>
                          <Textarea
                            label="Track data (JSON object, optional)"
                            value={option._track_data_json || ""}
                            onChange={(e) =>
                              updateButtonOption(
                                index,
                                "_track_data_json",
                                e.target.value as any,
                              )
                            }
                            placeholder={'{ "category": "cta" }'}
                            variant="rounded-md"
                            className="font-mono text-xs"
                            rows={3}
                          />
                          <div className="text-[11px] text-gray-500 mt-1">
                            If invalid JSON, it will be ignored on save.
                          </div>
                        </div>
                      </div>
                    )}
                  </div>

                  {/* New: Button visibility based on condition rules */}
                  <div className="mt-2 bg-gray-50 rounded p-2">
                    <div className="flex items-center justify-between">
                      <label className="inline-flex items-center gap-2">
                        <input
                          type="checkbox"
                          checked={!!option.hide_when}
                          onChange={(e) => {
                            const checked = e.target.checked;
                            updateButtonOption(index, "hide_when", checked);
                            if (!checked) {
                              updateButtonOption(
                                index,
                                "_visibility_mode",
                                "none",
                              );
                              updateButtonOption(
                                index,
                                "condition_rules_names",
                                undefined as any,
                              );
                              updateButtonOption(
                                index,
                                "_local_rules",
                                undefined as any,
                              );
                            } else if (
                              !option._visibility_mode ||
                              option._visibility_mode === "none"
                            ) {
                              updateButtonOption(
                                index,
                                "_visibility_mode",
                                "global",
                              );
                            }
                          }}
                        />
                        <span className="text-sm">
                          Hide this button under conditions
                        </span>
                      </label>
                      {option.hide_when && (
                        <div className="inline-flex items-center gap-3 text-xs">
                          <label className="inline-flex items-center gap-1">
                            <input
                              type="radio"
                              name={`vis-mode-${index}`}
                              checked={
                                (option._visibility_mode || "global") ===
                                "global"
                              }
                              onChange={() => {
                                updateButtonOption(
                                  index,
                                  "_visibility_mode",
                                  "global",
                                );
                              }}
                            />
                            Global rule
                          </label>
                          <label className="inline-flex items-center gap-1">
                            <input
                              type="radio"
                              name={`vis-mode-${index}`}
                              checked={option._visibility_mode === "local"}
                              onChange={() => {
                                updateButtonOption(
                                  index,
                                  "_visibility_mode",
                                  "local",
                                );
                              }}
                            />
                            Local rule
                          </label>
                        </div>
                      )}
                    </div>

                    {option.hide_when &&
                      (option._visibility_mode === "global" ||
                        !option._visibility_mode ||
                        option._visibility_mode === "none") && (
                        <div className="mt-2">
                          <label className="block text-xs text-gray-600 mb-1">
                            Select global condition rules by name
                          </label>
                          <SearchableGlobalRuleSelect
                            selectedNames={option.condition_rules_names || []}
                            rules={globalRules}
                            loading={isLoadingGlobalRules}
                            onQueryChange={setGlobalRulesQuery}
                            onChange={(names) =>
                              updateButtonOption(
                                index,
                                "condition_rules_names",
                                names,
                              )
                            }
                          />
                          <div className="text-[11px] text-gray-500 mt-1">
                            All selected rules must pass (AND).
                          </div>
                        </div>
                      )}

                    {option.hide_when &&
                      option._visibility_mode === "local" && (
                        <div className="mt-2">
                          <LocalConditionRulesEditor
                            rules={option._local_rules || []}
                            onChange={(rules) =>
                              updateButtonOption(
                                index,
                                "_local_rules",
                                rules as any,
                              )
                            }
                          />
                          <div className="text-[11px] text-gray-500 mt-1">
                            These are saved inline with the node and will be
                            evaluated by name only if you later publish them
                            globally.
                          </div>
                        </div>
                      )}
                  </div>

                  {/* New: Button Actions */}
                  <div className="mt-2 bg-blue-50 rounded p-2 border border-blue-200">
                    <div className="flex items-center justify-between mb-2">
                      <label className="inline-flex items-center gap-2">
                        <input
                          type="checkbox"
                          checked={
                            !!option.actions && option.actions.length > 0
                          }
                          onChange={(e) => {
                            const checked = e.target.checked;
                            if (!checked) {
                              updateButtonOption(index, "actions", undefined);
                            } else if (
                              !option.actions ||
                              option.actions.length === 0
                            ) {
                              updateButtonOption(index, "actions", []);
                            }
                          }}
                        />
                        <span className="text-sm font-medium text-blue-800">
                          Enable button actions
                        </span>
                      </label>
                    </div>

                    {option.actions && option.actions.length > 0 && (
                      <div className="space-y-3">
                        {option.actions.map((action, actionIndex) => (
                          <div
                            key={actionIndex}
                            className="flex flex-wrap gap-2 items-start p-2 bg-white rounded border"
                          >
                            <div className="flex-1 min-w-[150px]">
                              <label className="block text-xs text-gray-600 mb-1">
                                Action Name
                              </label>
                              <input
                                type="text"
                                value={action.name}
                                onChange={(e) => {
                                  const updatedActions = [...option.actions!];
                                  updatedActions[actionIndex] = {
                                    ...action,
                                    name: e.target.value,
                                  };
                                  updateButtonOption(
                                    index,
                                    "actions",
                                    updatedActions,
                                  );
                                }}
                                placeholder="e.g., persist_some_data_in_db"
                                className="w-full px-2 py-1 border border-gray-300 rounded text-xs focus:ring-1 focus:ring-blue-500 focus:border-blue-500"
                              />
                            </div>
                            <div className="flex-1 min-w-[120px]">
                              <label className="block text-xs text-gray-600 mb-1">
                                Event
                              </label>
                              <select
                                value={action.event}
                                onChange={(e) => {
                                  const updatedActions = [...option.actions!];
                                  updatedActions[actionIndex] = {
                                    ...action,
                                    event: e.target.value,
                                  };
                                  updateButtonOption(
                                    index,
                                    "actions",
                                    updatedActions,
                                  );
                                }}
                                className="w-full px-2 py-1 border border-gray-300 rounded text-xs focus:ring-1 focus:ring-blue-500 focus:border-blue-500"
                              >
                                <option value="click">click</option>
                                <option value="hover">hover</option>
                                <option value="focus">focus</option>
                                <option value="blur">blur</option>
                              </select>
                            </div>
                            <div className="flex-1 min-w-[120px]">
                              <label className="block text-xs text-gray-600 mb-1">
                                Type
                              </label>
                              <select
                                value={action.type}
                                onChange={(e) => {
                                  const updatedActions = [...option.actions!];
                                  updatedActions[actionIndex] = {
                                    ...action,
                                    type: e.target.value as
                                      | "sms"
                                      | "email"
                                      | "run_query",
                                  };
                                  updateButtonOption(
                                    index,
                                    "actions",
                                    updatedActions,
                                  );
                                }}
                                className="w-full px-2 py-1 border border-gray-300 rounded text-xs focus:ring-1 focus:ring-blue-500 focus:border-blue-500"
                              >
                                <option value="sms">SMS</option>
                                <option value="email">Email</option>
                                <option value="run_query">Run Query</option>
                              </select>
                            </div>
                            <button
                              type="button"
                              onClick={() => {
                                const updatedActions = option.actions!.filter(
                                  (_, i) => i !== actionIndex,
                                );
                                updateButtonOption(
                                  index,
                                  "actions",
                                  updatedActions.length > 0
                                    ? updatedActions
                                    : undefined,
                                );
                              }}
                              className="p-1 rounded hover:bg-red-50 text-red-600 self-end"
                              title="Remove action"
                            >
                              <Trash2 className="h-3 w-3" />
                            </button>
                          </div>
                        ))}
                        <button
                          type="button"
                          onClick={() => {
                            const newAction = {
                              name: "",
                              event: "click",
                              type: "run_query" as const,
                            };
                            const updatedActions = [
                              ...(option.actions || []),
                              newAction,
                            ];
                            updateButtonOption(
                              index,
                              "actions",
                              updatedActions,
                            );
                          }}
                          className="px-2 py-1 bg-blue-500 text-white rounded text-xs hover:bg-blue-600"
                        >
                          Add Action
                        </button>
                        <div className="text-[11px] text-blue-600 mt-1">
                          Actions will be executed when the specified event
                          occurs. The action name must match an existing action
                          in the corresponding actions table.
                        </div>
                      </div>
                    )}
                  </div>
                </div>
              ))}
            </div>
          </div>
        );

      case "email":
      case "phone":
        return (
          <div className="space-y-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Tip Text (
                {LANGUAGES.find((l) => l.code === selectedInputLanguage)
                  ?.label || selectedInputLanguage.toUpperCase()}
                )
              </label>
              <input
                type="text"
                value={getLocalizedFieldValue("tip", selectedInputLanguage)}
                onChange={(e) =>
                  setLocalizedFieldValue(
                    "tip",
                    selectedInputLanguage,
                    e.target.value,
                  )
                }
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                placeholder="Enter tip text"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Placeholder (
                {LANGUAGES.find((l) => l.code === selectedInputLanguage)
                  ?.label || selectedInputLanguage.toUpperCase()}
                )
              </label>
              <input
                type="text"
                value={getLocalizedFieldValue(
                  "placeholder",
                  selectedInputLanguage,
                )}
                onChange={(e) =>
                  setLocalizedFieldValue(
                    "placeholder",
                    selectedInputLanguage,
                    e.target.value,
                  )
                }
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                placeholder="Enter placeholder text"
              />
            </div>
          </div>
        );

      case "date-picker":
        return (
          <div className="space-y-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Tip Text (
                {LANGUAGES.find((l) => l.code === selectedInputLanguage)
                  ?.label || selectedInputLanguage.toUpperCase()}
                )
              </label>
              <input
                type="text"
                value={getLocalizedFieldValue("tip", selectedInputLanguage)}
                onChange={(e) =>
                  setLocalizedFieldValue(
                    "tip",
                    selectedInputLanguage,
                    e.target.value,
                  )
                }
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                placeholder="Enter tip text"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Placeholder (
                {LANGUAGES.find((l) => l.code === selectedInputLanguage)
                  ?.label || selectedInputLanguage.toUpperCase()}
                )
              </label>
              <input
                type="text"
                value={getLocalizedFieldValue(
                  "placeholder",
                  selectedInputLanguage,
                )}
                onChange={(e) =>
                  setLocalizedFieldValue(
                    "placeholder",
                    selectedInputLanguage,
                    e.target.value,
                  )
                }
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                placeholder="Enter placeholder text"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Weekdays
              </label>
              <div className="grid grid-cols-2 md:grid-cols-4 gap-2">
                {[
                  { label: "Mon", value: 0 },
                  { label: "Tue", value: 1 },
                  { label: "Wed", value: 2 },
                  { label: "Thu", value: 3 },
                  { label: "Fri", value: 4 },
                  { label: "Sat", value: 5 },
                  { label: "Sun", value: 6 },
                ].map((day) => {
                  const current = (formData.input_data
                    ?.weekdays as number[]) || [0, 1, 2, 3, 4, 5, 6];
                  const checked = current.includes(day.value);
                  return (
                    <label
                      key={day.value}
                      className="inline-flex items-center gap-2"
                    >
                      <input
                        type="checkbox"
                        checked={checked}
                        onChange={(e) => {
                          const prev =
                            (formData.input_data?.weekdays as number[]) || [];
                          const next = e.target.checked
                            ? Array.from(new Set([...prev, day.value]))
                            : prev.filter((v) => v !== day.value);
                          handleInputDataChange(
                            "weekdays",
                            next.sort((a, b) => a - b),
                          );
                        }}
                      />
                      <span>{day.label}</span>
                    </label>
                  );
                })}
              </div>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Max Days Ahead
                </label>
                <input
                  type="number"
                  value={formData.input_data?.max_days_ahead ?? ""}
                  onChange={(e) =>
                    handleInputDataChange(
                      "max_days_ahead",
                      e.target.value === ""
                        ? undefined
                        : parseInt(e.target.value),
                    )
                  }
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                  min="0"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Max Days Before
                </label>
                <input
                  type="number"
                  value={formData.input_data?.max_days_before ?? ""}
                  onChange={(e) =>
                    handleInputDataChange(
                      "max_days_before",
                      e.target.value === ""
                        ? undefined
                        : parseInt(e.target.value),
                    )
                  }
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                  min="0"
                />
              </div>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Dynamic Date Names (comma-separated)
                </label>
                <input
                  type="text"
                  value={
                    Array.isArray(formData.input_data?.quiet_periods_names)
                      ? (
                          formData.input_data?.quiet_periods_names as string[]
                        ).join(", ")
                      : ""
                  }
                  onChange={(e) =>
                    handleInputDataChange(
                      "quiet_periods_names",
                      e.target.value
                        .split(",")
                        .map((s) => s.trim())
                        .filter(Boolean),
                    )
                  }
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                  placeholder="e.g., New Year's Day, Christmas Day"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Dynamic Date Tags (comma-separated)
                </label>
                <input
                  type="text"
                  value={
                    Array.isArray(formData.input_data?.quiet_periods_tags)
                      ? (
                          formData.input_data?.quiet_periods_tags as string[]
                        ).join(", ")
                      : ""
                  }
                  onChange={(e) =>
                    handleInputDataChange(
                      "quiet_periods_tags",
                      e.target.value
                        .split(",")
                        .map((s) => s.trim())
                        .filter(Boolean),
                    )
                  }
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                  placeholder="comma-separated tags"
                />
              </div>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              <label className="inline-flex items-center gap-2">
                <input
                  type="checkbox"
                  checked={!!formData.input_data?.enable_years}
                  onChange={(e) =>
                    handleInputDataChange("enable_years", e.target.checked)
                  }
                />
                <span>Enable Years</span>
              </label>
              <label className="inline-flex items-center gap-2">
                <input
                  type="checkbox"
                  checked={!!formData.input_data?.enable_hours}
                  onChange={(e) =>
                    handleInputDataChange("enable_hours", e.target.checked)
                  }
                />
                <span>Enable Hours</span>
              </label>
              <label className="inline-flex items-center gap-2">
                <input
                  type="checkbox"
                  checked={!!formData.input_data?.enable_minutes}
                  onChange={(e) =>
                    handleInputDataChange("enable_minutes", e.target.checked)
                  }
                />
                <span>Enable Minutes</span>
              </label>
            </div>
          </div>
        );

      default:
        return null;
    }
  };

  return (
    <form onSubmit={handleSubmit} className="space-y-6">
      {/* Basic Node Information */}
      <div className="bg-white p-6 rounded-lg border border-gray-200">
        <div className="flex items-center justify-between">
          <h3 className="text-lg font-semibold text-gray-900">
            Basic Information
          </h3>
          <button
            type="button"
            onClick={() => setBasicCollapsed((v) => !v)}
            className="p-1 rounded hover:bg-gray-100"
            aria-label="Toggle section"
          >
            {basicCollapsed ? (
              <ChevronDown className="h-5 w-5" />
            ) : (
              <ChevronUp className="h-5 w-5" />
            )}
          </button>
        </div>
        {!basicCollapsed && (
          <div className="mt-4">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <Input
                label="Node Name"
                type="text"
                value={formData.node_name}
                onChange={(e) => handleInputChange("node_name", e.target.value)}
                required
                placeholder="e.g., greet, ask_email"
                disabled={
                  readOnly ||
                  (typeof node?.node_name === "string" &&
                    ["start", "end_conversation"].includes(
                      node.node_name.toLowerCase(),
                    ))
                }
              />

              {showScriptSelect && (
                <Select
                  label="Script"
                  value={formData.script_id || ""}
                  onChange={(e) =>
                    handleInputChange(
                      "script_id",
                      e.target.value ? parseInt(e.target.value) : undefined,
                    )
                  }
                >
                  <option value="">Global (not tied to script)</option>
                  {scripts.map((script) => (
                    <option key={script.id} value={script.id}>
                      {script.name}
                    </option>
                  ))}
                </Select>
              )}

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Message Sequence Number
                </label>
                <input
                  type="text"
                  value={formData.message_seq_no || ""}
                  onChange={(e) =>
                    handleInputChange(
                      "message_seq_no",
                      e.target.value || undefined,
                    )
                  }
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                  placeholder="Legacy system identifier (optional)"
                />
              </div>
            </div>
          </div>
        )}
      </div>

      {/* Messages */}
      <div className="bg-white p-6 rounded-lg border border-gray-200">
        <div className="flex items-center justify-between">
          <h3 className="text-lg font-semibold text-gray-900">Messages</h3>
          <button
            type="button"
            onClick={() => setMessagesCollapsed((v) => !v)}
            className="p-1 rounded hover:bg-gray-100"
            aria-label="Toggle section"
          >
            {messagesCollapsed ? (
              <ChevronDown className="h-5 w-5" />
            ) : (
              <ChevronUp className="h-5 w-5" />
            )}
          </button>
        </div>
        {!messagesCollapsed && (
          <div className="mt-4">
            <div className="mb-4">
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Language
              </label>
              <select
                value={selectedLanguage}
                onChange={(e) => setSelectedLanguage(e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
              >
                {LANGUAGES.map((lang) => (
                  <option key={lang.code} value={lang.code}>
                    {lang.label}
                  </option>
                ))}
                {Object.keys(formData.messages || {})
                  .filter((code) => !LANGUAGES.some((l) => l.code === code))
                  .map((code) => (
                    <option key={code} value={code}>
                      {code.toUpperCase()}
                    </option>
                  ))}
              </select>
            </div>

            <div className="mb-4">
              <label className="block text-sm font-medium text-gray-700 mb-1">
                {LANGUAGES.find((l) => l.code === selectedLanguage)?.label ||
                  selectedLanguage.toUpperCase()}{" "}
                Message
              </label>
              <div className="flex items-center gap-2 mb-2">
                <span className="text-xs text-gray-500">Syntax:</span>
                <div className="inline-flex rounded-md border border-gray-300 overflow-hidden">
                  <button
                    type="button"
                    className={`px-2 py-1 text-xs ${messageSyntax[selectedLanguage] !== "html" ? "bg-blue-50 text-blue-700" : "bg-white text-gray-700"}`}
                    onClick={() =>
                      handleSyntaxChange(selectedLanguage, "markdown")
                    }
                  >
                    Markdown
                  </button>
                  <button
                    type="button"
                    className={`px-2 py-1 text-xs ${messageSyntax[selectedLanguage] === "html" ? "bg-blue-50 text-blue-700" : "bg-white text-gray-700"}`}
                    onClick={() => handleSyntaxChange(selectedLanguage, "html")}
                  >
                    HTML
                  </button>
                </div>
              </div>

              {messageSyntax[selectedLanguage] === "html" ? (
                <ReactQuill
                  theme="snow"
                  value={formData.messages?.[selectedLanguage] || ""}
                  onChange={(val: string) =>
                    handleMessageChange(selectedLanguage, val)
                  }
                />
              ) : (
                <div data-color-mode="light">
                  <MDEditor
                    value={formData.messages?.[selectedLanguage] || ""}
                    onChange={(val?: string) =>
                      handleMessageChange(selectedLanguage, val || "")
                    }
                    height={220}
                  />
                </div>
              )}

              {/* Preview controls */}
              <div className="mt-2">
                <div className="flex items-center justify-between mb-1">
                  <span className="text-xs font-medium text-gray-600">
                    Preview
                  </span>
                  <button
                    type="button"
                    className={`px-2 py-1 text-xs rounded ${previewLoading[selectedLanguage] ? "bg-gray-100 text-gray-400" : "bg-blue-600 text-white hover:bg-blue-700"}`}
                    onClick={() => previewMessage(selectedLanguage)}
                    disabled={!!previewLoading[selectedLanguage]}
                  >
                    {previewLoading[selectedLanguage]
                      ? "Rendering…"
                      : "Render Preview"}
                  </button>
                </div>

                {previewError[selectedLanguage] && (
                  <div className="text-[11px] text-red-600 mb-2">
                    {previewError[selectedLanguage]}
                  </div>
                )}
                {messagePreview[selectedLanguage] !== undefined && (
                  <div className="border border-gray-200 rounded p-3 bg-gray-50">
                    {messageSyntax[selectedLanguage] === "markdown" ? (
                      <MarkdownPreview
                        source={messagePreview[selectedLanguage]}
                      />
                    ) : (
                      <div
                        className="prose max-w-none"
                        dangerouslySetInnerHTML={{
                          __html: messagePreview[selectedLanguage],
                        }}
                      />
                    )}
                  </div>
                )}
              </div>
            </div>
          </div>
        )}
      </div>

      {/* Input Configuration */}
      <div className="bg-white p-6 rounded-lg border border-gray-200">
        <div className="flex items-center justify-between">
          <h3 className="text-lg font-semibold text-gray-900">
            Input Configuration
          </h3>
          <button
            type="button"
            onClick={() => setInputCollapsed((v) => !v)}
            className="p-1 rounded hover:bg-gray-100"
            aria-label="Toggle section"
          >
            {inputCollapsed ? (
              <ChevronDown className="h-5 w-5" />
            ) : (
              <ChevronUp className="h-5 w-5" />
            )}
          </button>
        </div>
        {!inputCollapsed && (
          <div className="mt-4">
            <div className="mb-4">
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Input Language
              </label>
              <select
                value={selectedInputLanguage}
                onChange={(e) => setSelectedInputLanguage(e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
              >
                {getAvailableInputLanguages().map((code) => (
                  <option key={code} value={code}>
                    {LANGUAGES.find((l) => l.code === code)?.label ||
                      code.toUpperCase()}
                  </option>
                ))}
              </select>
            </div>

            <div className="mb-4">
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Input Type
              </label>
              <select
                value={formData.input_type || ""}
                onChange={(e) => {
                  const nextType = e.target.value || undefined;
                  const prevType = formData.input_type;

                  // Store current input_data in cache under previous type before switching
                  if (prevType) {
                    inputDataCacheRef.current[prevType] =
                      formData.input_data || {};
                  }

                  // Determine restored input_data for the new type if it exists in cache
                  const restored = nextType
                    ? inputDataCacheRef.current[nextType] || {}
                    : {};

                  handleInputChange("input_type", nextType);
                  setFormData((prev) => ({ ...prev, input_data: restored }));

                  // Keep button options in sync when toggling to/from buttons
                  if (nextType === "buttons") {
                    const opts = Array.isArray((restored as any)?.options)
                      ? (restored as any).options
                      : [];
                    setButtonOptions((opts as any[]).map(prepareOptionForUI));
                  } else {
                    setButtonOptions([]);
                  }
                }}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
              >
                <option value="">No input required</option>
                {INPUT_TYPES.map((type) => (
                  <option key={type.value} value={type.value}>
                    {type.label}
                  </option>
                ))}
              </select>
            </div>

            {formData.input_type && renderInputDataFields()}
          </div>
        )}
      </div>

      {/* Tracking Configuration */}
      <div className="bg-white p-6 rounded-lg border border-gray-200">
        <div className="flex items-center justify-between">
          <h3 className="text-lg font-semibold text-gray-900">
            Tracking Configuration
          </h3>
          <button
            type="button"
            onClick={() => setTrackingCollapsed((v) => !v)}
            className="p-1 rounded hover:bg-gray-100"
            aria-label="Toggle section"
          >
            {trackingCollapsed ? (
              <ChevronDown className="h-5 w-5" />
            ) : (
              <ChevronUp className="h-5 w-5" />
            )}
          </button>
        </div>
        {!trackingCollapsed && (
          <div className="mt-4">
            <div className="flex justify-between items-center mb-3">
              <p className="text-sm text-gray-600">
                Configure click tracking for this node
              </p>
              <button
                type="button"
                onClick={addTrackConfig}
                className="px-3 py-1 bg-green-500 text-white rounded-md hover:bg-green-600 text-sm"
              >
                Add Track
              </button>
            </div>

            {trackConfigs.map((track, index) => (
              <div key={index} className="flex gap-2 mb-2">
                <select
                  value={track.type}
                  onChange={(e) =>
                    updateTrackConfig(index, "type", e.target.value)
                  }
                  className="px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                >
                  <option value="click">Click</option>
                </select>
                <input
                  type="text"
                  value={track.html_element}
                  onChange={(e) =>
                    updateTrackConfig(index, "html_element", e.target.value)
                  }
                  placeholder="CSS selector (e.g., a[href^='mailto:'])"
                  className="flex-1 px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                />
                <button
                  type="button"
                  onClick={() => removeTrackConfig(index)}
                  className="px-3 py-2 bg-red-500 text-white rounded-lg hover:bg-red-600"
                >
                  Remove
                </button>
              </div>
            ))}
          </div>
        )}
      </div>

      {extraSectionBeforeActions}

      {/* Actions Section */}
      {node?.id ? (
        <div className="mt-6">
          <NodeActionsSection nodeId={node.id} readOnly={readOnly} />
        </div>
      ) : null}

      {/* Form Actions */}
      <div className="flex justify-end gap-3">
        {node && onDelete && (
          <button
            type="button"
            onClick={onDelete}
            disabled={isSubmitting || readOnly}
            className="p-2 text-red-600 bg-red-50/50 border border-red-200 rounded-lg hover:bg-red-50 disabled:opacity-50"
            title="Delete node"
            aria-label="Delete node"
          >
            <Trash2 className="h-5 w-5" />
          </button>
        )}
        <button
          type="button"
          onClick={onCancel}
          disabled={isSubmitting}
          className="px-4 py-2 text-gray-700 bg-gray-100 border border-gray-300 rounded-lg hover:bg-gray-200 disabled:opacity-50"
        >
          Cancel
        </button>
        <button
          type="submit"
          disabled={isSubmitting || readOnly}
          className="px-4 py-2 bg-blue-500 text-white rounded-lg hover:bg-blue-600 disabled:opacity-50"
        >
          {isSubmitting ? "Saving..." : node ? "Update Node" : "Create Node"}
        </button>
      </div>
    </form>
  );
}

function SearchableGlobalRuleSelect({
  selectedNames,
  rules,
  loading,
  onQueryChange,
  onChange,
}: {
  selectedNames: string[];
  rules: ConditionRule[];
  loading: boolean;
  onQueryChange: (q: string) => void;
  onChange: (names: string[]) => void;
}) {
  const [open, setOpen] = React.useState(false);
  const [query, setQuery] = React.useState("");
  const ref = React.useRef<HTMLDivElement | null>(null);

  React.useEffect(() => {
    onQueryChange(query);
  }, [query]);

  const selected = new Set(selectedNames);
  const toggle = (name: string) => {
    const next = new Set(selected);
    if (next.has(name)) next.delete(name);
    else next.add(name);
    onChange(Array.from(next));
  };

  React.useEffect(() => {
    const onDoc = (e: MouseEvent) => {
      if (!ref.current) return;
      if (!ref.current.contains(e.target as unknown as HTMLElement))
        setOpen(false);
    };
    document.addEventListener("mousedown", onDoc);
    return () => document.removeEventListener("mousedown", onDoc);
  }, []);

  return (
    <div className="relative" ref={ref}>
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        className="px-2 py-1 border rounded bg-white text-left min-w-[16rem]"
      >
        {selectedNames.length > 0
          ? `${selectedNames.length} rule(s) selected`
          : "Select rules..."}
      </button>
      {open && (
        <div className="absolute z-10 mt-1 w-[22rem] bg-white border rounded shadow">
          <div className="p-2 border-b">
            <input
              type="text"
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              placeholder="Search rule name..."
              className="w-full px-2 py-1 border rounded text-sm"
            />
          </div>
          <div className="max-h-60 overflow-auto">
            {loading ? (
              <div className="px-3 py-2 text-sm text-gray-500">Loading…</div>
            ) : (
              (rules || []).map((r) => (
                <label
                  key={r.id}
                  className="flex items-center gap-2 px-3 py-2 hover:bg-gray-50 text-sm"
                >
                  <input
                    type="checkbox"
                    checked={selected.has(((r as any).name as string) || "")}
                    onChange={() => toggle(((r as any).name as string) || "")}
                  />
                  <div className="flex-1">
                    <div className="font-medium">
                      {(r as any).name || `rule_${r.id}`}
                    </div>
                    {(r as any).description && (
                      <div className="text-[11px] text-gray-500">
                        {(r as any).description}
                      </div>
                    )}
                  </div>
                </label>
              ))
            )}
            {!loading && (!rules || rules.length === 0) && (
              <div className="px-3 py-2 text-sm text-gray-500">No results</div>
            )}
          </div>
        </div>
      )}
    </div>
  );
}

function LocalConditionRulesEditor({
  rules,
  onChange,
}: {
  rules: ConditionRule[];
  onChange: (rules: ConditionRule[]) => void;
}) {
  const [items, setItems] = React.useState<ConditionRule[]>(rules || []);
  React.useEffect(() => {
    setItems(rules || []);
  }, [rules]);

  const add = () => {
    const next: ConditionRule = {
      id: Date.now(),
      type: "any_input",
      values: null,
      sql_query: null,
      db_name: null,
      logical_operator: undefined,
      use_logical_not: false,
      user_input_node_id: null,
    } as any;
    const list = [...items, next];
    setItems(list);
    onChange(list);
  };
  const update = (idx: number, chg: Partial<ConditionRule>) => {
    const list = items.map((r, i) =>
      i === idx ? ({ ...r, ...chg } as any) : r,
    );
    setItems(list);
    onChange(list);
  };
  const remove = (idx: number) => {
    const list = items.filter((_, i) => i !== idx);
    setItems(list);
    onChange(list);
  };

  return (
    <div className="space-y-2">
      <div className="flex items-center justify-between">
        <span className="text-xs font-medium text-gray-700">
          Local Condition Rules
        </span>
        <button
          type="button"
          className="px-2 py-1 text-xs bg-blue-600 text-white rounded"
          onClick={add}
        >
          Add Rule
        </button>
      </div>
      {items.length === 0 ? (
        <div className="text-xs text-gray-600">No rules</div>
      ) : (
        <div className="space-y-2">
          {items.map((rule, idx) => (
            <div key={rule.id} className="bg-white border rounded p-2">
              <ConditionRuleInlineEditor
                rule={rule}
                onChange={(chg) => update(idx, chg as any)}
                onDelete={() => remove(idx)}
              />
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

function ConditionRuleInlineEditor({
  rule,
  onChange,
  onDelete,
}: {
  rule: ConditionRule;
  onChange: (chg: Partial<ConditionRule>) => void;
  onDelete: () => void;
}) {
  const [local, setLocal] = React.useState<ConditionRule>({ ...rule });
  React.useEffect(() => {
    setLocal({ ...rule });
  }, [rule.id]);
  const apply = (chg: Partial<ConditionRule>) =>
    setLocal((prev) => ({ ...prev, ...chg }) as any);
  const save = () => onChange(local as any);
  return (
    <div>
      <div className="grid grid-cols-2 gap-2">
        <label className="text-xs text-gray-600">
          Type
          <select
            className="mt-1 w-full border rounded px-2 py-1 text-sm"
            value={local.type}
            onChange={(e) => apply({ type: e.target.value })}
          >
            <option value="any_input">any_input</option>
            <option value="auto">auto</option>
            <option value="input_in">input_in</option>
            <option value="input_not_in">input_not_in</option>
            <option value="sql">sql</option>
            <option value="daytime">daytime</option>
            <option value="quiet_periods">quiet_periods</option>
            <option value="variable_in">variable_in</option>
            <option value="variable_not_in">variable_not_in</option>
            <option value="variable_is_falsy">variable_is_falsy</option>
            <option value="variable_is_truthy">variable_is_truthy</option>
            <option value="variable_is_null">variable_is_null</option>
            <option value="variable_is_not_null">variable_is_not_null</option>
            <option value="variables_expression">variables_expression</option>
            <option value="entrypoint">entrypoint</option>
            <option value="timer">timer</option>
          </select>
        </label>
        <label className="text-xs text-gray-600">
          Logical operator
          <select
            className="mt-1 w-full border rounded px-2 py-1 text-sm"
            value={(local.logical_operator || "") as any}
            onChange={(e) =>
              apply({ logical_operator: (e.target.value || undefined) as any })
            }
          >
            <option value="">AND (default)</option>
            <option value="OR">OR</option>
          </select>
        </label>
        <label className="text-xs text-gray-600 flex items-center gap-2">
          Not
          <input
            type="checkbox"
            className="mt-1"
            checked={!!local.use_logical_not}
            onChange={(e) => apply({ use_logical_not: e.target.checked })}
          />
        </label>
      </div>
      {/* Values editors subset */}
      {local.type === "input_in" || local.type === "input_not_in" ? (
        <div className="mt-2">
          <ValuesListEditor
            values={
              Array.isArray(local.values)
                ? (local.values as any[])
                : Array.isArray((local.values as any)?.values)
                  ? (local.values as any).values || []
                  : []
            }
            onChange={(newValues) => apply({ values: newValues } as any)}
            placeholder="Enter value"
            label="Values"
          />
        </div>
      ) : null}
      {local.type === "variable_in" || local.type === "variable_not_in" ? (
        <div className="space-y-2 mt-2">
          <label className="text-xs text-gray-600">
            Variable name
            <input
              className="mt-1 w-full border rounded px-2 py-1 text-sm"
              value={(local.values as any)?.var_name || ""}
              onChange={(e) =>
                apply({
                  values: {
                    ...(local.values as any),
                    var_name: e.target.value,
                    values: (local.values as any)?.values || [],
                  },
                } as any)
              }
            />
          </label>
          <ValuesListEditor
            values={
              Array.isArray((local.values as any)?.values)
                ? (local.values as any).values || []
                : []
            }
            onChange={(newValues) =>
              apply({
                values: {
                  ...(local.values as any),
                  values: newValues,
                },
              } as any)
            }
            placeholder="Enter value"
            label="Allowed values"
          />
        </div>
      ) : null}
      <div className="flex items-center gap-2 mt-3">
        <button
          type="button"
          className="px-2 py-1 text-xs bg-blue-600 text-white rounded"
          onClick={save}
        >
          Save
        </button>
        <button
          type="button"
          className="px-2 py-1 text-xs border rounded text-red-600"
          onClick={onDelete}
        >
          Delete
        </button>
      </div>
    </div>
  );
}
